// Practice ticket: plug the loose Ethernet cable into the laptop (original SVG artwork).
//
// Mouse / touch: drag the loose plug onto a port. Keyboard / tap: activate the plug to pick it
// up, then activate a port (Escape puts it down). The component never changes the simulator
// itself: it asks `onPort(portId)`, which runs the real endpoint change and returns
// { ok, message }. Only `ok` connects; any other drop snaps the plug back.
import { h, svg } from './h.js';

const W = 400, H = 300;
const JACK = { x: 55, y: 92 };
const START = { x: 160, y: 160 };
// port centres (viewBox units); ids match PRACTICE_PORTS in src/domains/support/endpoint.js
const PORT_AT = { usb: { x: 90, y: 252 }, hdmi: { x: 200, y: 252 }, ethernet: { x: 310, y: 252 } };
const pct = (v, of) => `${(v / of) * 100}%`;

function portGlyph(id, x, y) {
  if (id === 'usb') return svg('rect', { x: x - 14, y: y - 5, width: 28, height: 10, rx: 5, class: 'rz-cable-hole' });
  if (id === 'hdmi') return svg('path', { d: `M${x - 18} ${y - 7}h36v8l-5 6h-26l-5-6z`, class: 'rz-cable-hole' });
  return svg('g', {}, svg('path', { d: `M${x - 15} ${y - 11}h30v16h-8v6h-14v-6h-8z`, class: 'rz-cable-hole' }),
    ...[0, 1, 2, 3, 4, 5].map((i) => svg('rect', { x: x - 11 + i * 4, y: y - 9, width: 2, height: 5, class: 'rz-cable-pin' })));
}

/**
 * @param {object} o
 * @param {{id:string,label:string}[]} o.ports
 * @param {boolean} o.connected  current simulator link state
 * @param {(portId:string) => {ok:boolean, message:string}} o.onPort
 */
export function cableActivity({ ports, connected, onPort }) {
  let picked = false;
  let pos = connected ? { x: PORT_AT.ethernet.x, y: PORT_AT.ethernet.y - 28 } : { ...START };

  const cable = svg('path', { class: 'rz-cable-wire', fill: 'none' });
  const art = svg('svg', { viewBox: `0 0 ${W} ${H}`, class: 'rz-cable-art', 'aria-hidden': 'true', focusable: 'false' },
    // wall plate with the jack (the cable's other end is already plugged in here)
    svg('rect', { x: 18, y: 36, width: 74, height: 92, rx: 8, class: 'rz-cable-plate' }),
    svg('rect', { x: JACK.x - 14, y: 62, width: 28, height: 22, rx: 3, class: 'rz-cable-hole' }),
    svg('rect', { x: JACK.x - 9, y: 70, width: 18, height: 14, rx: 2, class: 'rz-cable-plug-art' }),
    svg('text', { x: 55, y: 28, class: 'rz-cable-label', 'text-anchor': 'middle' }, 'Wall jack'),
    // laptop
    svg('rect', { x: 212, y: 24, width: 166, height: 104, rx: 8, class: 'rz-cable-laptop' }),
    svg('rect', { x: 222, y: 34, width: 146, height: 84, rx: 3, class: 'rz-cable-screen' }),
    svg('text', { x: 295, y: 72, class: 'rz-cable-screen-text', 'text-anchor': 'middle' }, connected ? 'Connected' : 'No internet'),
    svg('text', { x: 295, y: 92, class: 'rz-cable-screen-text', 'text-anchor': 'middle' }, connected ? 'Ethernet: link up' : 'Cable unplugged'),
    svg('path', { d: 'M196 128h204l-12 14h-180z', class: 'rz-cable-laptop' }),
    // the laptop's left side, enlarged so the ports are easy to see and hit
    svg('rect', { x: 18, y: 196, width: 364, height: 92, rx: 12, class: 'rz-cable-side' }),
    svg('text', { x: 30, y: 214, class: 'rz-cable-label' }, 'Laptop — left side (enlarged)'),
    ...ports.map((p) => portGlyph(p.id, PORT_AT[p.id].x, PORT_AT[p.id].y)),
    svg('circle', { cx: PORT_AT.ethernet.x + 26, cy: PORT_AT.ethernet.y - 6, r: 4, class: `rz-cable-led ${connected ? 'on' : ''}` }),
    cable);

  const plug = h('button', {
    type: 'button', class: `rz-cable-plug ${connected ? 'seated' : ''}`, 'data-testid': 'practice-cable-plug',
    'aria-pressed': 'false', disabled: connected ? true : null,
    'aria-label': connected ? 'Ethernet cable — plugged into the Ethernet port' : 'Loose end of the Ethernet cable. Drag it to a port, or press to pick it up.',
  }, h('span', { class: 'rz-cable-plug-body', 'aria-hidden': 'true' }), h('span', { class: 'rz-cable-plug-text', 'aria-hidden': 'true' }, 'RJ-45'));

  const portButtons = ports.map((p) => h('button', {
    type: 'button', class: 'rz-cable-port', 'data-port': p.id, 'data-testid': `practice-port-${p.id}`,
    'aria-label': p.label, disabled: connected ? true : null,
    style: { left: pct(PORT_AT[p.id].x, W), top: pct(PORT_AT[p.id].y, H) },
    onclick: () => { if (!dragMoved) choose(p.id, true); },
  }, h('span', { class: 'rz-cable-port-label' }, p.label.replace(/ port.*$/i, '').replace(/ \(.*\)$/, ''))));

  const feedback = h('p', { class: 'rz-cable-feedback', role: 'status', 'aria-live': 'polite', 'data-testid': 'practice-cable-feedback' },
    connected ? 'Cable connected to the Ethernet port. The link light is on.' : 'The cable is plugged into the wall but not into the laptop.');

  const root = h('div', { class: `rz-cable ${connected ? 'connected' : ''}`, 'data-testid': 'practice-cable', 'data-connected': connected ? 'true' : 'false' },
    h('p', { class: 'rz-cable-help', id: 'rz-cable-help' }, connected ? 'Done — now test the connection.' : 'Drag the loose plug onto the right port on the laptop. Keyboard: press the plug, then press a port.'),
    h('div', { class: 'rz-cable-stage', 'aria-describedby': 'rz-cable-help' }, art, ...portButtons, plug),
    feedback);

  const place = () => {
    plug.style.left = pct(pos.x, W); plug.style.top = pct(pos.y, H);
    const mid = { x: (JACK.x + pos.x) / 2, y: Math.max(JACK.y, pos.y) + 50 };
    cable.setAttribute('d', `M${JACK.x} ${JACK.y} C${JACK.x} ${mid.y} ${mid.x} ${mid.y + 20} ${pos.x} ${pos.y + 10}`);
  };
  const setPicked = (on) => {
    picked = on;
    plug.setAttribute('aria-pressed', on ? 'true' : 'false');
    root.classList.toggle('picking', on);
  };
  const say = (text, kind) => { feedback.textContent = text; feedback.className = `rz-cable-feedback ${kind || ''}`; };

  function choose(portId, viaKeyboard) {
    if (connected) return;
    if (viaKeyboard && !picked) { say('First pick up the loose cable (press the plug), then choose a port.', 'bad'); return; }
    const r = onPort(portId);
    if (r?.ok) return; // the caller redraws with the real, connected endpoint
    setPicked(false);
    pos = { ...START }; place();
    say(r?.message || 'Not connected.', 'bad');
  }

  // ---- pointer drag (mouse, pen, touch)
  let drag = null; let dragMoved = false;
  const toView = (e) => { const b = art.getBoundingClientRect(); return { x: ((e.clientX - b.left) / b.width) * W, y: ((e.clientY - b.top) / b.height) * H }; };
  const portUnder = (e) => portButtons.find((b) => { const r = b.getBoundingClientRect(); return e.clientX >= r.left - 6 && e.clientX <= r.right + 6 && e.clientY >= r.top - 6 && e.clientY <= r.bottom + 6; });
  plug.addEventListener('pointerdown', (e) => {
    if (connected) return;
    drag = { x: e.clientX, y: e.clientY }; dragMoved = false;
    try { plug.setPointerCapture(e.pointerId); } catch { /* synthetic or already-released pointer */ }
  });
  plug.addEventListener('pointermove', (e) => {
    if (!drag) return;
    if (!dragMoved && Math.hypot(e.clientX - drag.x, e.clientY - drag.y) < 6) return;
    if (!dragMoved) { dragMoved = true; root.classList.add('dragging'); }
    const v = toView(e);
    pos = { x: Math.max(10, Math.min(W - 10, v.x)), y: Math.max(10, Math.min(H - 10, v.y)) };
    place();
    const over = portUnder(e);
    for (const b of portButtons) b.classList.toggle('hover', b === over);
  });
  const end = (e, cancelled) => {
    if (!drag) return;
    const moved = dragMoved; drag = null;
    root.classList.remove('dragging');
    for (const b of portButtons) b.classList.remove('hover');
    if (!moved) return; // a click: handled by onclick
    const over = cancelled ? null : portUnder(e);
    if (over) { picked = true; choose(over.dataset.port, false); } else {
      pos = { ...START }; place();
      say('Not connected. Drop the plug onto a port on the laptop.', 'bad');
    }
    setTimeout(() => { dragMoved = false; }, 0);
  };
  plug.addEventListener('pointerup', (e) => end(e, false));
  plug.addEventListener('pointercancel', (e) => end(e, true));

  // ---- keyboard / tap alternative
  plug.addEventListener('click', () => {
    if (dragMoved || connected) return;
    setPicked(!picked);
    say(picked ? 'Cable picked up. Now choose a port on the laptop (Escape to put it down).' : 'Cable put down.', '');
    if (picked) portButtons[0]?.focus();
  });
  root.addEventListener('keydown', (e) => { if (e.key === 'Escape' && picked) { setPicked(false); say('Cable put down.', ''); plug.focus(); } });

  place();
  return root;
}
